import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Close, EditOutlined, Home, InfoOutlined } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { 
    Box,
    Button,
    Checkbox,
    Chip,
    ClickAwayListener,
    IconButton, 
    InputAdornment,
    ListItemText,
    MenuItem,
    Paper,
    Popper,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { DataGrid, GridToolbarContainer, GridToolbarFilterButton, GridSearchIcon } from "@mui/x-data-grid";
import { useGetActiveOrganizationsQuery, useUpdateOrganizationMutation } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { showToast } from "utils/reusableToast";

import EditOrgModal from "./editOrgModal";
import ExportModal from "./ExportModal";
import { tableStyles } from "./tableStyles";

import Pagination from "../../pages/homeDashboard/components/Pagination/Pagination";

const RECORDS_PER_PAGE = 6;

const OrgsList = () => {
    const { data: platformData } = useGetPlatformsQuery();
    const platformId = platformData?._id;
    const { data: orgListData, isLoading, isError } = useGetActiveOrganizationsQuery({ platformId });
    const [updateOrganization, { isLoading: isUpdating }] = useUpdateOrganizationMutation();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialSearch = searchParams.get("search") || "";
    const [search, setSearch] = useState(initialSearch);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [tempSelectedCountries, setTempSelectedCountries] = useState([]);
    const [countrySearch, setCountrySearch] = useState("");
    const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
    const countryAnchorRef = useRef(null);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [expandedOwnersRow, setExpandedOwnersRow] = useState(null);
    const [expandedApproversRow, setExpandedApproversRow] = useState(null);
    const [expandedCountriesRow, setExpandedCountriesRow] = useState(null);
    const data = orgListData || [];

    const location = useLocation();
    const navigate = useNavigate();
    const [value, setValue] = React.useState(location.pathname);

    const handleChange = (event, newValue) => {
        setValue(newValue);
        navigate(newValue);
    };
    // When the query param changes (e.g. via link), update the search state
    useEffect(() => {
        setSearch(searchParams.get("search") || "");
    }, [searchParams]);

    // When the search box changes, update the query param
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        setSearchParams(value ? { search: value } : {});
    };

    // Get user permissions from Redux store
    const userPermissions = useSelector(state => state.auth?.permissions || []);
    const isAdmin = useMemo(() => {
        return userPermissions.some(perm => perm.userType === 'Owner' || perm.userType === 'Admin');
    }, [userPermissions]);

    // Set document title
    useEffect(() => {
        document.title = "GitHub (ECR) Global Organizations";
    }, []);

    // Extract unique countries from all organizations
    const allCountries = useMemo(() => {
        const countries = new Set();
        data.forEach(org => {
            if (org?.countries) {
                if (typeof org.countries === 'string') {
                    // Handle comma-separated string
                    org.countries.split(',').forEach(country => {
                        const trimmed = country.trim();
                        if (trimmed) countries.add(trimmed);
                    });
                } else if (Array.isArray(org.countries)) {
                    // Handle array
                    org.countries.forEach(country => {
                        if (country) countries.add(country);
                    });
                }
            }
        });
        return Array.from(countries).sort();
    }, [data]);

    // Initialize selected countries to all countries
    useEffect(() => {
        if (allCountries.length > 0 && selectedCountries.length === 0) {
            setSelectedCountries(allCountries);
            setTempSelectedCountries(allCountries);
        }
    }, [allCountries, selectedCountries.length]);
    // Filtered data with search and country filters
    const filteredData = useMemo(() => {
        return data.filter((org) => {
            // Text search filter - only search in org name and org owners
            const matchesSearch = 
                (org?.name && org?.name.toLowerCase().includes(search.toLowerCase())) ||
                (Array.isArray(org?.owners_email ?? org?.owners) && (org?.owners_email ?? org?.owners).join(", ").toLowerCase().includes(search.toLowerCase()));
            
            if (!matchesSearch) return false;
            
            // Country filter - if no countries selected or no country field, show all
            if (selectedCountries.length === 0 || selectedCountries.length === allCountries.length) {
                return true;
            }
            
            // Check if org has any of the selected countries
            if (org?.countries) {
                let orgCountries = [];
                if (typeof org.countries === 'string') {
                    orgCountries = org.countries.split(',').map(c => c.trim());
                } else if (Array.isArray(org.countries)) {
                    orgCountries = org.countries;
                }
                return orgCountries.some(country => selectedCountries.includes(country));
            }
            
            // If org has no countries, don't show it when filtering
            return false;
        });
    }, [data, search, selectedCountries, allCountries.length]);

    // Sort the filtered results so admin orgs come first
    const sortedFilteredData = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            const isAAdmin = userPermissions.some(
                perm =>
                    String(perm.orgId).trim() === String(a._id).trim() &&
                    (perm.userType === "Owner" || perm.userType === "Admin")
            );
            const isBAdmin = userPermissions.some(
                perm =>
                    String(perm.orgId).trim() === String(b._id).trim() &&
                    (perm.userType === "Owner" || perm.userType === "Admin")
            );
            if (isAAdmin === isBAdmin) return 0;
            return isAAdmin ? -1 : 1;
        });
    }, [filteredData, userPermissions]);

    // Pagination logic
    const nPages = Math.ceil(sortedFilteredData.length / RECORDS_PER_PAGE);
    const indexOfLastRecord = currentPage * RECORDS_PER_PAGE;
    const indexOfFirstRecord = indexOfLastRecord - RECORDS_PER_PAGE;
    const currentRecords = sortedFilteredData.slice(indexOfFirstRecord, indexOfLastRecord);

    // DataGrid columns configuration
    const columns = useMemo(() => {
        const baseColumns = [
            {
                field: "name",
                headerName: "Org Name",
                flex: 1.2,
                minWidth: 200,
                renderCell: (params) => (
                    <Tooltip title={params.value || "-"}>
                        <strong style={{ cursor: "default", fontSize: "13px" }}>{params.value || "-"}</strong>
                    </Tooltip>
                ),
            },
            {
                field: "owners_email",
                headerName: "Org Owners",
                flex: 1.5,
                minWidth: 220,
                valueGetter: (params) => {
                    const ownersList = params.row.owners_email || [];
                    return Array.isArray(ownersList) ? ownersList.join(", ") : "";
                },
                renderCell: (params) => {
                    const ownersList = params.row.owners_email || [];
                    if (ownersList.length === 0) return "-";
                    
                    const isExpanded = expandedOwnersRow === params.row.id;
                    
                    if (ownersList.length <= 2) {
                        return (
                            <Tooltip title={ownersList.join(", ")}>
                                <div style={{ 
                                    fontSize: "13px", 
                                    cursor: "default", 
                                    width: "100%", 
                                    overflow: "hidden" 
                                }}>
                                    {ownersList.join(", ")}
                                </div>
                            </Tooltip>
                        );
                    }
                    
                    const displayText = isExpanded 
                        ? ownersList.join(", ") 
                        : ownersList.slice(0, 2).join(", ");
                    const fullText = ownersList.join(", ");
                    
                    return (
                        <Tooltip title={fullText}>
                            <div style={{ 
                                fontSize: "13px", 
                                cursor: "default", 
                                width: "100%", 
                                overflow: "hidden" 
                            }}>
                                {displayText}
                                {!isExpanded && (
                                    <span 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedOwnersRow(params.row.id);
                                        }}
                                        style={{ 
                                            color: "#1976d2", 
                                            marginLeft: "4px",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            fontSize: "12px"
                                        }}
                                    >
                                        +{ownersList.length - 2} more
                                    </span>
                                )}
                            </div>
                        </Tooltip>
                    );
                },
            },
            {
                field: "url",
                headerName: "Org URL",
                flex: 0.8,
                minWidth: 100,
                filterable: false,
                renderCell: (params) => {
                    return params.value ? (
                        <a href={params.value} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2", fontSize: "13px" }}>
                            Org Link
                        </a>
                    ) : <span style={{ fontSize: "13px" }}>-</span>;
                },
            },
            {
                field: "service_owner",
                headerName: "Service Owner",
                flex: 1.2,
                minWidth: 180,
                renderCell: (params) => (
                    <Tooltip title={params.value || "-"}>
                        <span style={{ cursor: "default", fontSize: "13px" }}>{params.value || "-"}</span>
                    </Tooltip>
                ),
            },
            {
                field: "approvers",
                headerName: "Approvers",
                flex: 1.3,
                minWidth: 180,
                valueGetter: (params) => {
                    const approversList = params.row.approvers || [];
                    return Array.isArray(approversList) ? approversList.join(", ") : "";
                },
                renderCell: (params) => {
                    const approversList = params.row.approvers || [];
                    if (approversList.length === 0) return <span style={{ fontSize: "13px" }}>-</span>;
                    
                    const isExpanded = expandedApproversRow === params.row.id;
                    
                    if (approversList.length <= 2) {
                        return (
                            <Tooltip title={approversList.join(", ")}>
                                <div style={{ 
                                    fontSize: "13px", 
                                    cursor: "default", 
                                    width: "100%", 
                                    overflow: "hidden" 
                                }}>
                                    {approversList.join(", ")}
                                </div>
                            </Tooltip>
                        );
                    }
                    
                    const displayText = isExpanded 
                        ? approversList.join(", ") 
                        : approversList.slice(0, 2).join(", ");
                    const fullText = approversList.join(", ");
                    
                    return (
                        <Tooltip title={fullText}>
                            <div style={{ 
                                fontSize: "13px", 
                                cursor: "default", 
                                width: "100%", 
                                overflow: "hidden" 
                            }}>
                                {displayText}
                                {!isExpanded && (
                                    <span 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedApproversRow(params.row.id);
                                        }}
                                        style={{ 
                                            color: "#1976d2", 
                                            marginLeft: "4px",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            fontSize: "12px"
                                        }}
                                    >
                                        +{approversList.length - 2} more
                                    </span>
                                )}
                            </div>
                        </Tooltip>
                    );
                },
            },
            {
                field: "countries",
                headerName: "Countries",
                flex: 1,
                minWidth: 150,
                filterable: false,
                renderCell: (params) => {
                    const rawValue = params.value;
                    if (!rawValue || rawValue === "-") {
                        return <span style={{ fontSize: "13px" }}>-</span>;
                    }
                    
                    // Parse countries - handle both string and array
                    let countriesList = [];
                    if (typeof rawValue === 'string') {
                        countriesList = rawValue.split(',').map(c => c.trim()).filter(c => c);
                    } else if (Array.isArray(rawValue)) {
                        countriesList = rawValue.filter(c => c);
                    } else {
                        return <span style={{ fontSize: "13px" }}>{String(rawValue)}</span>;
                    }
                    
                    if (countriesList.length === 0) {
                        return <span style={{ fontSize: "13px" }}>-</span>;
                    }
                    
                    const isExpanded = expandedCountriesRow === params.row.id;
                    
                    if (countriesList.length <= 2) {
                        const displayText = countriesList.join(", ");
                        return (
                            <Tooltip title={displayText}>
                                <div style={{ 
                                    fontSize: "13px", 
                                    cursor: "default", 
                                    width: "100%", 
                                    overflow: "hidden" 
                                }}>
                                    {displayText}
                                </div>
                            </Tooltip>
                        );
                    }
                    
                    const displayCountries = isExpanded 
                        ? countriesList.join(", ") 
                        : countriesList.slice(0, 2).join(", ");
                    const fullText = countriesList.join(", ");
                    
                    return (
                        <Tooltip title={fullText}>
                            <div style={{ 
                                fontSize: "13px", 
                                cursor: "default", 
                                width: "100%", 
                                overflow: "hidden" 
                            }}>
                                {displayCountries}
                                {!isExpanded && (
                                    <span 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedCountriesRow(params.row.id);
                                        }}
                                        style={{ 
                                            color: "#1976d2", 
                                            marginLeft: "4px",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            fontSize: "12px"
                                        }}
                                    >
                                        +{countriesList.length - 2} more
                                    </span>
                                )}
                            </div>
                        </Tooltip>
                    );
                },
            },
            {
                field: "copilot_link",
                headerName: "Copilot Requests Info",
                flex: 1,
                minWidth: 150,
                filterable: false,
                renderCell: (params) => {
                    return params.value ? (
                        <a href={params.value} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2", fontSize: "13px" }}>
                            Copilot Link
                        </a>
                    ) : <span style={{ fontSize: "13px" }}>-</span>;
                },
            },
            {
                field: "org_website",
                headerName: "Org Website",
                flex: 0.8,
                minWidth: 120,
                filterable: false,
                renderCell: (params) => {
                    return params.value ? (
                        <a href={params.value} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2", fontSize: "13px" }}>
                            Org Website
                        </a>
                    ) : <span style={{ fontSize: "13px" }}>-</span>;
                },
            },
        ];

        // Add actions column only for admins
        if (isAdmin) {
            baseColumns.push({
                field: "actions",
                headerName: "Actions",
                flex: 0.5,
                minWidth: 80,
                sortable: false,
                filterable: false,
                renderCell: (params) => {
                    const canEdit = userPermissions.find(
                        perm =>
                            String(perm.orgId).trim() === String(params.row._id).trim() &&
                            (perm.userType === "Owner" || perm.userType === "Admin")
                    );
                    return canEdit ? (
                        <Tooltip title="Edit organization">
                            <IconButton
                                onClick={() => handleEditClick(params.row)}
                                size="small"
                                color="primary"
                            >
                                <EditOutlined />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <span style={{ color: "#bbb" }}>-</span>
                    );
                },
            });
        }

        return baseColumns;
    }, [isAdmin, userPermissions, expandedOwnersRow, expandedApproversRow, expandedCountriesRow]);

    // DataGrid rows - map organization data to match column field names
    const rows = useMemo(() => {
        return currentRecords.map((org) => {
            // Extract countries as string
            let countriesDisplay = "";
            if (org?.countries) {
                if (typeof org.countries === 'string') {
                    countriesDisplay = org.countries;
                } else if (Array.isArray(org.countries)) {
                    countriesDisplay = org.countries.join(", ");
                }
            }

            return {
                id: org._id, // DataGrid requires an 'id' field
                _id: org._id,
                name: org.name,
                owners: org.owners_email?.length > 0 ? org.owners_email : org.owners || [],
                owners_email: org.owners_email,
                url: org.additional_info?.github_org_link || "",
                service_owner: org.service_owner || "",
                approvers: org.approvers || [],
                countries: countriesDisplay,
                copilot_link: org.additional_info?.custom_copilot_page || "",
                org_website: org.additional_info?.custom_org_website || "",
                additional_info: org.additional_info,
                // Keep full org object for edit functionality
                ...org
            };
        });
    }, [currentRecords]);

    // Custom locale text for DataGrid
    const customLocaleText = {
        noRowsLabel: "No organizations found matching your search criteria.",
    };

    const handleModalSave = async (orgId, formData) => {
        try {
            // Prepare the data to be sent to the API
            const updateData = {
                name: formData.name,
                approvers: formData.approvers,
                service_owner: formData.service_owner,
                // owners_email: formData.owners_email, // Uncomment if you allow editing owners_email
                additional_info: {
                    github_org_link: formData.url || "",
                    custom_copilot_page: formData.copilot_link || "",
                    custom_org_website: formData.org_website || ""
                }
            };
            // Call the update API
            await updateOrganization({
                orgId,
                updateData
            }).unwrap();
            showToast({
                title: `Changes saved!`,
                description: "Your updates to the organization have been applied.",
                type: "success",
                duration: 5000
            });
            setIsModalOpen(false);
            setSelectedOrg(null);
        } catch (error) {
            console.error('Failed to update organization:', error);
            showToast({
                title: `Update Failed!`,
                description: error?.data?.message || "Failed to update organization data. Please try again later.",
                type: "error",
                duration: 5000,
            });
        }
    };

    // Handle edit button click
    const handleEditClick = (org) => {
        setSelectedOrg(org);
        setIsModalOpen(true);
    };

    // Handle modal close
    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedOrg(null);
    };

    // Reset to page 1 when search or country filter changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [search, selectedCountries]);

    // Filter countries based on search
    const filteredCountries = useMemo(() => {
        if (!allCountries || allCountries.length === 0) return [];
        if (!countrySearch) return allCountries;
        return allCountries.filter((country) =>
            country.toLowerCase().includes(countrySearch.toLowerCase())
        );
    }, [allCountries, countrySearch]);

    // Handle country selection (temporary)
    const handleCountryToggle = (country) => {
        setTempSelectedCountries((prev) => {
            const newSelection = prev.includes(country)
                ? prev.filter((c) => c !== country)
                : [...prev, country];
            // Don't allow deselecting all countries
            return newSelection.length === 0 ? prev : newSelection;
        });
    };

    // Handle select all/deselect all
    const handleSelectAll = () => {
        if (tempSelectedCountries.length === allCountries?.length) {
            // Don't allow deselecting all - keep at least one (first country)
            if (allCountries?.length > 0) {
                setTempSelectedCountries([allCountries[0]]);
            }
        } else {
            setTempSelectedCountries(allCountries || []);
        }
    };

    // Handle close dropdown (cancel changes)
    const handleCancelDropdown = () => {
        setTempSelectedCountries(selectedCountries);
        setCountryDropdownOpen(false);
        setCountrySearch("");
    };

    // Handle apply selection (Done button)
    const handleApplySelection = () => {
        setSelectedCountries(tempSelectedCountries);
        setCountryDropdownOpen(false);
        setCountrySearch("");
    };

    // Export to CSV function
    const handleExportToCSV = (selectedColumns) => {
        try {
            // Column mapping
            const columnMapping = {
                name: "Org Name",
                owners: "Org Owners",
                url: "Org URL",
                service_owner: "Service Owner",
                approvers: "Approvers",
                countries: "Countries",
                copilot_link: "Copilot Requests Info",
                org_website: "Org Website"
            };

            // Prepare CSV headers based on selected columns
            const headers = selectedColumns.map(colId => columnMapping[colId]);

            // Prepare CSV rows
            const rows = sortedFilteredData.map(org => {
                const rowData = [];

                selectedColumns.forEach(colId => {
                    switch(colId) {
                        case 'name':
                            rowData.push(org?.name || "");
                            break;
                        case 'owners':
                            const ownersList = Array.isArray(org?.owners_email) && org.owners_email.length > 0
                                ? org.owners_email
                                : Array.isArray(org?.owners) && org.owners.length > 0
                                    ? org.owners
                                    : [];
                            rowData.push(ownersList.join("; "));
                            break;
                        case 'url':
                            rowData.push(org?.additional_info?.github_org_link || "");
                            break;
                        case 'service_owner':
                            rowData.push(org?.service_owner || "");
                            break;
                        case 'approvers':
                            const approversList = Array.isArray(org?.approvers) ? org.approvers : [];
                            rowData.push(approversList.join("; "));
                            break;
                        case 'countries':
                            let countriesList = "";
                            if (org?.countries) {
                                if (typeof org.countries === 'string') {
                                    countriesList = org.countries;
                                } else if (Array.isArray(org.countries)) {
                                    countriesList = org.countries.join(", ");
                                }
                            }
                            rowData.push(countriesList);
                            break;
                        case 'copilot_link':
                            rowData.push(org?.additional_info?.custom_copilot_page || "");
                            break;
                        case 'org_website':
                            rowData.push(org?.additional_info?.custom_org_website || "");
                            break;
                        default:
                            rowData.push("");
                    }
                });

                return rowData;
            });

            // Convert to CSV string
            const csvContent = [
                headers.map(h => `"${h}"`).join(","),
                ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
            ].join("\n");

            // Create blob and download
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `organizations_export_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            showToast({
                title: "Export Successful",
                description: `Exported ${sortedFilteredData.length} organization${sortedFilteredData.length !== 1 ? 's' : ''} with ${selectedColumns.length} column${selectedColumns.length !== 1 ? 's' : ''}`,
                type: "success",
                duration: 3000
            });
        } catch (error) {
            console.error("Export failed:", error);
            showToast({
                title: "Export Failed",
                description: "Failed to export organizations. Please try again.",
                type: "error",
                duration: 5000
            });
        }
    };

    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        // Clean up on unmount
        return () => {
            document.body.style.overflow = "";
        };
    }, [isModalOpen]);

    // Custom toolbar with filter button
    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ 
                padding: "10px 4px 10px 11px",
                backgroundColor: "#ffffff",
                display: "flex",
                gap: 2
            }}>
                <GridToolbarFilterButton 
                    sx={{ 
                        color: "#ffffff",
                        backgroundColor: "#0b756c",
                        fontWeight: 600,
                        fontSize: "14px",
                        padding: "8px 16px",
                        borderRadius: "4px",
                        "&:hover": {
                            backgroundColor: "#085a50"
                        }
                    }} 
                />
                <Typography sx={{ ml: 2, color: "#666", fontSize: "13px", alignSelf: "center" }}>
                    Click to add column filters
                </Typography>
            </GridToolbarContainer>
        );
    }

    return (
        <div style={tableStyles.container}>
            <div style={tableStyles.header}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="Return to Dashboard">
                        <IconButton
                            onClick={() => navigate('/dashboard')}
                            sx={{
                                mr: 2,
                                color: '#0b756c',
                                '&:hover': { backgroundColor: 'rgba(11, 117, 108, 0.08)' }
                            }}
                        >
                            <Home sx={{ fontSize: 32 }} /> {/* Increased icon size */}
                        </IconButton>
                    </Tooltip>
                    <h2 style={tableStyles.title}>Deloitte GitHub (ECR) Global Organizations</h2>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Tooltip title="Search by organization name or owner emails">
                        <div style={tableStyles.searchBox}>
                            <GridSearchIcon style={{ ...tableStyles.searchIcon, fontSize: 28 }} /> {/* Increased icon size */}
                            <input
                                type="text"
                                placeholder="Search by name or owners..."
                                value={search}
                                onChange={handleSearchChange}
                                style={tableStyles.searchInput}
                            />
                            {search && (
                                <Close
                                    onClick={() => {
                                        setSearch("");
                                        setSearchParams({});
                                    }}
                                    style={{
                                        position: "absolute",
                                        right: 10,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "#888",
                                        cursor: "pointer",
                                        fontSize: 22,
                                        background: "#fff"
                                    }}
                                />
                            )}
                        </div>
                    </Tooltip>
                    
                    {/* Country Filter Dropdown */}
                    {allCountries && allCountries.length > 1 && (
                        <Box sx={{ minWidth: "220px" }}>
                            <Button
                                ref={countryAnchorRef}
                                onClick={() => {
                                    setTempSelectedCountries(selectedCountries);
                                    setCountryDropdownOpen(!countryDropdownOpen);
                                }}
                                endIcon={<ExpandMoreIcon />}
                                variant="outlined"
                                fullWidth
                                sx={{
                                    backgroundColor: "#fafafa",
                                    borderColor: "#e0e0e0",
                                    color: "#555",
                                    textTransform: "none",
                                    justifyContent: "space-between",
                                    height: "40px",
                                    fontSize: "0.875rem",
                                    "&:hover": {
                                        backgroundColor: "#f0f0f0",
                                        borderColor: "#e0e0e0",
                                    },
                                }}
                            >
                                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                                    {selectedCountries.length === 0
                                        ? "Select countries"
                                        : selectedCountries.length === allCountries.length
                                        ? "All Countries"
                                        : `${selectedCountries.length} ${selectedCountries.length === 1 ? 'country' : 'countries'}`}
                                </Typography>
                            </Button>
                            <Popper
                                open={countryDropdownOpen}
                                anchorEl={countryAnchorRef.current}
                                placement="bottom-start"
                                style={{ zIndex: 1300, width: countryAnchorRef.current?.offsetWidth }}
                            >
                                <ClickAwayListener onClickAway={() => countryDropdownOpen && handleCancelDropdown()}>
                                    <Paper
                                        elevation={8}
                                        sx={{
                                            mt: 0.5,
                                            maxHeight: 400,
                                            display: "flex",
                                            flexDirection: "column",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        {/* Search Input */}
                                        <Box sx={{ p: 2, pb: 1 }}>
                                            <TextField
                                                size="small"
                                                placeholder="Search countries..."
                                                value={countrySearch}
                                                onChange={(e) => setCountrySearch(e.target.value)}
                                                fullWidth
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: countrySearch && (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setCountrySearch("")}
                                                            >
                                                                <Close fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "6px",
                                                    },
                                                }}
                                            />
                                        </Box>

                                        {/* Select All / Deselect All */}
                                        <Box
                                            sx={{
                                                px: 2,
                                                pb: 1,
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                borderBottom: "1px solid #e0e0e0",
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                onClick={handleSelectAll}
                                                sx={{
                                                    textTransform: "none",
                                                    fontSize: "0.75rem",
                                                    color: "#1565c0",
                                                }}
                                            >
                                                {tempSelectedCountries.length === allCountries.length
                                                    ? "Deselect All"
                                                    : "Select All"}
                                            </Button>
                                            <Typography variant="caption" sx={{ color: "#666" }}>
                                                {tempSelectedCountries.length} / {allCountries.length}
                                            </Typography>
                                        </Box>

                                        {/* Countries List */}
                                        <Box
                                            sx={{
                                                maxHeight: 250,
                                                overflow: "auto",
                                                px: 1,
                                            }}
                                        >
                                            {filteredCountries.length === 0 ? (
                                                <Box sx={{ p: 2, textAlign: "center" }}>
                                                    <Typography variant="body2" sx={{ color: "#999" }}>
                                                        No countries found
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                filteredCountries.map((country) => (
                                                    <MenuItem
                                                        key={country}
                                                        onClick={() => handleCountryToggle(country)}
                                                        sx={{
                                                            borderRadius: "6px",
                                                            mx: 0.5,
                                                            my: 0.25,
                                                        }}
                                                    >
                                                        <Checkbox
                                                            checked={tempSelectedCountries.includes(country)}
                                                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                            sx={{ mr: 1 }}
                                                        />
                                                        <ListItemText
                                                            primary={country}
                                                            primaryTypographyProps={{
                                                                fontSize: "0.875rem",
                                                            }}
                                                        />
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Box>

                                        {/* Footer with Done button */}
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderTop: "1px solid #e0e0e0",
                                                display: "flex",
                                                justifyContent: "flex-end",
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={handleApplySelection}
                                                sx={{
                                                    textTransform: "none",
                                                    backgroundColor: "#1565c0",
                                                    "&:hover": {
                                                        backgroundColor: "#0d47a1",
                                                    },
                                                }}
                                            >
                                                Done
                                            </Button>
                                        </Box>
                                    </Paper>
                                </ClickAwayListener>
                            </Popper>
                        </Box>
                    )}
                    <Tooltip
                        title={
                            <div>
                                <strong>Search and Filter:</strong>
                                <ul style={{ margin: "8px 0 0 16px", padding: 0, fontSize: "13px" }}>
                                    <li><strong>Search bar:</strong> Organization Name & Org Owners</li>
                                    <li><strong>Filters button:</strong> Service Owner & Approvers</li>
                                    <li><strong>Country dropdown:</strong> Filter by countries</li>
                                </ul>
                            </div>
                        }
                        arrow
                        placement="right"
                    >
                        <InfoOutlined
                            style={{
                                color: "#1976d2",
                                cursor: "pointer",
                                fontSize: 20,
                                background: "#fff"
                            }}
                        />
                    </Tooltip>
                    
                    {/* Export to CSV Button */}
                    <Tooltip title="Export organizations to CSV" arrow>
                        <span>
                            <Button
                                onClick={() => setExportModalOpen(true)}
                                variant="contained"
                                startIcon={<FileDownloadOutlinedIcon />}
                                disabled={sortedFilteredData.length === 0}
                                sx={{
                                    backgroundColor: "#0b756c",
                                    color: "#fff",
                                    textTransform: "none",
                                    height: "40px",
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                    "&:hover": {
                                        backgroundColor: "#085a50",
                                    },
                                    "&:disabled": {
                                        backgroundColor: "#ccc",
                                        color: "#888"
                                    }
                                }}
                            >
                                Export CSV
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            </div>

            {isError ? (
                <div style={{ color: "#d32f2f", textAlign: "center", padding: "20px" }}>
                    Failed to load organizations. Please try again later.
                </div>
            ) : (
                <>
                    <Box sx={{ 
                        width: "100%", 
                        height: 650,
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
                    }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            loading={isLoading}
                            disableSelectionOnClick
                            hideFooter={true}
                            disableColumnFilter={false}
                            disableColumnMenu={false}
                            disableExtendRowFullWidth={false}
                            columnBuffer={2}
                            localeText={customLocaleText}
                            components={{
                                Toolbar: CustomToolbar,
                            }}
                            getRowHeight={(params) => {
                                // If row is expanded, use auto height to fit content
                                if (expandedOwnersRow === params.id || expandedApproversRow === params.id || expandedCountriesRow === params.id) {
                                    return "auto";
                                }
                                // Default row height
                                return 80;
                            }}
                            sx={{
                                border: "none",
                                "& .MuiDataGrid-main": {
                                    overflow: "visible",
                                },
                                "& .MuiDataGrid-columnHeaders": {
                                    backgroundColor: "#0b756c",
                                    borderBottom: "2px solid #0b756c",
                                    minHeight: "45px !important",
                                    maxHeight: "45px !important",
                                    color: "#ffffff",
                                },
                                "& .MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: 600,
                                    fontSize: "12px !important",
                                    color: "#ffffff !important",
                                },
                                "& .MuiDataGrid-columnHeader": {
                                    color: "#ffffff !important",
                                    outline: "none !important",
                                    paddingLeft: "16px !important",
                                },
                                "& .MuiDataGrid-columnHeaderTitleContainer": {
                                    color: "#ffffff !important",
                                },
                                "& .MuiDataGrid-iconButtonContainer": {
                                    "& .MuiIconButton-root": {
                                        color: "#ffffff !important",
                                    },
                                },
                                "& .MuiDataGrid-menuIconButton": {
                                    color: "#ffffff !important",
                                },
                                "& .MuiDataGrid-menuIcon": {
                                    color: "#ffffff !important",
                                },
                                "& .MuiDataGrid-sortIcon": {
                                    color: "#ffffff !important",
                                },
                                "& .MuiDataGrid-columnSeparator": {
                                    color: "#ffffff !important",
                                },
                                "& .MuiDataGrid-row": {
                                    borderBottom: "1px solid rgb(234, 234, 234)",
                                    boxShadow: "none !important",
                                    "&:hover": {
                                        backgroundColor: "#f6f8fa",
                                    },
                                    "&:nth-of-type(even)": {
                                        backgroundColor: "#f8fcfa",
                                    },
                                    "&:nth-of-type(odd)": {
                                        backgroundColor: "#ffffff",
                                    },
                                    marginBottom: "0px !important",
                                },
                                "& .MuiDataGrid-cell": {
                                    fontSize: "13px !important",
                                    color: "#24292f",
                                    padding: "16px 16px 16px 20px",
                                    lineHeight: 1.5,
                                    verticalAlign: "top",
                                    whiteSpace: "normal",
                                    wordWrap: "break-word",
                                },
                                "& .MuiDataGrid-virtualScroller": {
                                    overflowX: "auto",
                                },
                            }}
                        />
                    </Box>

                    <div style={tableStyles.pagination}>
                        <div style={tableStyles.paginationInfo}>
                            Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, sortedFilteredData.length)} of {sortedFilteredData.length} organizations
                        </div>
                        {nPages > 1 && (
                            <Pagination
                                nPages={nPages}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                            />
                        )}
                    </div>
                </>
            )}
            <EditOrgModal
                org={selectedOrg}
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onSave={handleModalSave}
                isLoading={isUpdating}
            />
            <ExportModal
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                onExport={handleExportToCSV}
                totalRecords={sortedFilteredData.length}
            />
        </div>
    );
};


export default OrgsList;

